import { RouteName } from "constant/routeNames";
import { useCallback, useEffect, useState } from "react";
import { FaPlay } from "react-icons/fa";
import { HiX } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setVideoStart, setVideoSuccess } from "redux/VideoPlayerRedux";

// Netflix/YouTube-style "up next" auto-play countdown.
const AUTO_PLAY_SECONDS = 8;
const RING_RADIUS = 20;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function PlayerShowNext({ videoDetails, show, setShowNext }: any) {
  const { currentVideo } = useSelector((state: any) => state?.video);
  const dispatch = useDispatch<any>();
  const [showDetails, setShowDetails] = useState<any | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(AUTO_PLAY_SECONDS);
  const [dismissed, setDismissed] = useState<boolean>(false);
  const navigate = useNavigate();
  const { slug }: any = useParams();

  const findSeasonAndEpisode = () => {
    //console.log("videoDetails", videoDetails);
    //console.log("CurrentVideo", currentVideo);
    // find the season and episode
    if (videoDetails && currentVideo) {
      const seasonIdx: any = videoDetails?.seasons?.findIndex(
        (s: any) => s.id === currentVideo.season
      );

      if (seasonIdx !== -1 && seasonIdx !== undefined) {
        const currentSeason = videoDetails?.seasons[seasonIdx];
        const episodeIdx: any = videoDetails?.seasons[
          seasonIdx
        ]?.episodes?.findIndex((e: any) => e.id === currentVideo.id);
        //console.log(currentSeason);
        //console.log(episodeIdx);
        if (episodeIdx !== -1) {
          const nextEpisodeIdx: number = episodeIdx + 1;

          //console.log("Next Episode Number", nextEpisodeIdx);

          if (nextEpisodeIdx < currentSeason?.episodes?.length - 1) {
            // There is a next episode in the current season
            const nextEpisode = currentSeason?.episodes[nextEpisodeIdx];
            // console.log(currentSeason);
            const value = {
              ...nextEpisode,
              tv_series_id: videoDetails.id,
              season: currentSeason.id,
              poster_image: nextEpisode.thumbnail,
            };
            console.log("Next episode in the current season:", value);
            setShowDetails(value);
          } else {
            // There are no more episodes in the current season, check for the next season
            const nextSeasonIdx = seasonIdx + 1;

            if (nextSeasonIdx < videoDetails?.seasons?.length) {
              // There is a next season
              const nextSeason = videoDetails.seasons[nextSeasonIdx];
              const nextEpisode = nextSeason.episodes[0];
              const value = {
                ...nextEpisode,
                tv_series_id: videoDetails.id,
                season: nextSeason.id,
                poster_image: nextEpisode.thumbnail,
              };
              setShowDetails(value);
              console.log("Next season and its first episode:", value);
            } else {
              console.log("No more episodes or seasons.");
            }
          }
        }
      }
    }
  };

  useEffect(() => {
    findSeasonAndEpisode();
  }, [currentVideo, videoDetails]);

  // Reset the countdown whenever a new "up next" episode is computed (a
  // fresh instance of the prompt), so it always starts from the top.
  useEffect(() => {
    setSecondsLeft(AUTO_PLAY_SECONDS);
    setDismissed(false);
  }, [showDetails?.id]);

  const playNext = useCallback(() => {
    if (!showDetails) return;
    dispatch(setVideoStart());
    dispatch(setVideoSuccess(showDetails));
    navigate(
      RouteName.WatchTVSeries.replace(":slug", slug).replace(
        ":stream",
        showDetails?.stream_key
      )
    );
    setShowDetails(null);
    setShowNext(false);
  }, [showDetails, slug, dispatch, navigate, setShowNext]);

  // Auto-play the next episode once the countdown reaches zero, unless the
  // user has dismissed the prompt.
  useEffect(() => {
    if (!showDetails || !show || dismissed) return;
    if (secondsLeft <= 0) {
      playNext();
      return;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [showDetails, show, dismissed, secondsLeft, playNext]);

  const handleDismiss = (e: any) => {
    e.stopPropagation();
    setDismissed(true);
    setShowNext(false);
  };

  const progress = (AUTO_PLAY_SECONDS - secondsLeft) / AUTO_PLAY_SECONDS;
  const ringDashOffset = RING_CIRCUMFERENCE * (1 - progress);

  return (
    <>
      {showDetails && !dismissed && (
        <div
          className={`${
            show ? "playnext" : ""
          } group relative z-50 flex w-[260px] max-w-[85vw] cursor-pointer items-center gap-3 overflow-hidden rounded-xl bg-[#14161a]/95 p-3 shadow-2xl ring-1 ring-white/10 backdrop-blur-md transition-opacity duration-500 ease-in-out hover:ring-white/20 max-sm:left-10 max-sm:w-[220px]`}
          onClick={playNext}
          role="button"
          tabIndex={0}
          aria-label={`Play next episode in ${secondsLeft} seconds`}
        >
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white/70 transition-colors hover:bg-black/80 hover:text-white"
          >
            <HiX size={14} />
          </button>

          {/* Thumbnail with the auto-play countdown ring overlaid on top,
              Netflix/YouTube style. */}
          <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-lg max-sm:h-14 max-sm:w-20">
            <img
              src={showDetails.poster_image}
              alt="poster"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/55">
              <div className="relative flex h-11 w-11 items-center justify-center">
                <svg
                  width="44"
                  height="44"
                  viewBox="0 0 44 44"
                  className="-rotate-90"
                >
                  <circle
                    cx="22"
                    cy="22"
                    r={RING_RADIUS}
                    strokeWidth="3"
                    fill="none"
                    className="stroke-white/20"
                  />
                  <circle
                    cx="22"
                    cy="22"
                    r={RING_RADIUS}
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                    className="stroke-[var(--red)] transition-[stroke-dashoffset] duration-1000 ease-linear"
                    style={{
                      strokeDasharray: RING_CIRCUMFERENCE,
                      strokeDashoffset: ringDashOffset,
                    }}
                  />
                </svg>
                <span className="absolute text-[13px] font-semibold text-white group-hover:hidden">
                  {secondsLeft}
                </span>
                <FaPlay className="absolute hidden text-[12px] text-white group-hover:block" />
              </div>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              Up Next
            </p>
            <p className="truncate text-[14px] font-semibold text-white max-sm:text-[12px]">
              {showDetails.title?.length > 28
                ? `${showDetails.title.slice(0, 28)}...`
                : showDetails.title}
            </p>
            <p className="truncate text-[11px] text-gray-400 max-sm:text-[10px]">
              {showDetails.season_name}
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default PlayerShowNext;
